import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { RegenLogo } from '@/components/RegenLogo';
import { fmtDay } from '@/lib/dates';
import { Clock, Lock, Dumbbell, UtensilsCrossed, Heart, TrendingUp, Scale, Target, Lightbulb, MessageSquare } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PILLARS = [
  { name: 'Nutrition', desc: 'Flexible, sustainable fuel built around your life.' },
  { name: 'Movement', desc: 'Training that builds strength without burnout.' },
  { name: 'Recovery', desc: 'Sleep, fasting & active recovery to regenerate.' },
  { name: 'Mindset', desc: 'Daily discipline that compounds into identity.' },
  { name: 'Supplements & Longevity', desc: 'Smart support for a longer, healthier life.' },
];

export default function PreparationHome() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [ob, setOb] = useState(null);
  const [tips, setTips] = useState([]);
  const isTrial = user?.subscription_tier === 'regen_plus';

  useEffect(() => {
    api.get('/onboarding').then(r => setOb(r.data.data || {})).catch(() => setOb({}));
    api.get('/tips').then(r => setTips(r.data.slice(0, 3))).catch(() => {});
  }, []);

  // Self-serve YourReGen+ trial: programs generate in the background — poll until ready.
  useEffect(() => {
    if (!isTrial) return;
    const t = setInterval(() => { refresh(); }, 4000);
    return () => clearInterval(t);
  }, [isTrial, refresh]);

  const d = ob || {};
  const photos = d.photos || {};
  const cw = d.current_weight_kg ?? user?.current_weight_kg;
  const gw = d.target_weight_kg ?? user?.goal_weight_kg;

  const locked = [
    { label: 'Training', icon: Dumbbell, color: '#00C8FF' },
    { label: 'Diet', icon: UtensilsCrossed, color: '#65A30D' },
    { label: 'Recovery Plan', icon: Heart, color: '#A78BFA' },
    { label: 'Progress Tracking', icon: TrendingUp, color: '#FFB800' },
  ];

  return (
    <div className="space-y-5" data-testid="preparation-home">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-black text-2xl leading-tight">Welcome, {user?.name?.split(' ')[0] || 'Athlete'} 👋</h1>
          <div className="text-xs text-muted-foreground mt-0.5">Your transformation starts here.</div>
        </div>
        <div className="flex items-center gap-2">
          <RegenLogo size="sm" />
          <button onClick={() => nav('/app/coach')} className="w-10 h-10 rounded-full bg-[var(--c-film)] hover:bg-[var(--c-film)] flex items-center justify-center" data-testid="prep-coach-btn">
            <MessageSquare className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Status card */}
      {user?.pending_block?.approved ? (
      <div className="regen-card-elevated p-6 blueprint-grid" data-testid="prep-starts-card">
        <div className="flex items-center gap-2 label-mini text-[var(--c-green-ink)]"><Clock className="w-3.5 h-3.5" /> Programme Approved</div>
        <h2 className="font-display font-black text-2xl mt-2" data-testid="prep-starts-date">Your programme starts on {fmtDay(user.pending_block.start_date, { weekday: 'long', day: 'numeric', month: 'long' })}.</h2>
        <p className="text-sm text-muted-foreground mt-2.5 leading-relaxed">Coach JP has approved your training and nutrition plan. Use the time to plan your meals and schedule your sessions — everything unlocks on your start date. You can already log sleep, fasting and weight.</p>
      </div>
      ) : (
      <div className="regen-card-elevated p-6 blueprint-grid" data-testid="prep-status-card">
        <div className="flex items-center gap-2 label-mini text-[#00C8FF]"><Clock className="w-3.5 h-3.5" /> Program Status</div>
        <h2 className="font-display font-black text-2xl mt-2">Your personalised programme is being prepared.</h2>
        <div className="text-sm text-muted-foreground mt-2.5 leading-relaxed space-y-2" data-testid="prep-message">
          <p>Your training and nutrition programmes have been generated based on your goals and onboarding information.</p>
          <p>Coach JP is now reviewing your personalised programme to ensure everything is right for you.</p>
          <p>You'll receive a notification as soon as your programme has been approved and is ready to access.</p>
        </div>
        <div className="mt-4 inline-flex items-center gap-2 pill bg-[#FFB800]/15 text-[#FFB800]" data-testid="prep-status">Status: Awaiting Coach JP's Review</div>
        <p className="text-sm mt-3">Thank you for your patience. Your journey starts soon!</p>
      </div>
      )}

      {/* Locked tiles */}
      <div className="grid grid-cols-2 gap-3">
        {locked.map(l => (
          <div key={l.label} data-testid={`prep-locked-${l.label.replace(/\s+/g,'-').toLowerCase()}`}
            className="regen-card-elevated p-4 relative opacity-80">
            <div className="flex items-center gap-2">
              <l.icon className="w-4 h-4" style={{ color: l.color }} />
              <span className="text-sm font-semibold">{l.label}</span>
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 pill bg-[var(--c-film)] text-muted-foreground text-[11px]">
              <Lock className="w-3 h-3" /> Locked
            </div>
          </div>
        ))}
      </div>

      {/* Weight & target */}
      {(cw || gw) && (
        <div className="regen-card-elevated p-5" data-testid="prep-weight-card">
          <div className="label-mini mb-3">Your Numbers</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-[var(--c-film)] p-4">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Scale className="w-3.5 h-3.5" /> Current Weight</div>
              <div className="font-mono text-3xl mt-1">{cw ? `${cw}` : '—'}<span className="text-base text-muted-foreground ml-1">kg</span></div>
            </div>
            <div className="rounded-xl bg-[var(--c-film)] p-4">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Target className="w-3.5 h-3.5" /> Target Weight</div>
              <div className="font-mono text-3xl mt-1 text-[var(--c-green-ink)]">{gw ? `${gw}` : '—'}<span className="text-base text-muted-foreground ml-1">kg</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Progress photos */}
      {(photos.front || photos.side || photos.back) && (
        <div className="regen-card-elevated p-5" data-testid="prep-photos-card">
          <div className="label-mini mb-3">Your Starting Photos</div>
          <div className="grid grid-cols-3 gap-2.5">
            {['front', 'side', 'back'].map(a => (
              <div key={a} className="aspect-[3/4] rounded-xl overflow-hidden bg-[var(--c-film)] relative">
                {photos[a] ? <img src={`${BACKEND}${photos[a]}`} alt={a} className="w-full h-full object-cover" /> : null}
                <span className="absolute bottom-0 inset-x-0 text-center text-[10px] font-semibold py-1 bg-black/40 capitalize">{a}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recovery education tips */}
      {tips.length > 0 && (
        <div className="regen-card-elevated p-5" data-testid="prep-tips-card">
          <div className="flex items-center gap-2 label-mini mb-3 text-[#FFB800]"><Lightbulb className="w-3.5 h-3.5" /> While You Wait — Learn The Method</div>
          <div className="space-y-3">
            {tips.map(t => (
              <div key={t.id} className="rounded-xl bg-[var(--c-film)] p-3.5">
                <div className="text-sm font-semibold">{t.title}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-3">{t.body || t.content}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* YourReGen Protocol overview */}
      <div className="regen-card-elevated p-5" data-testid="prep-method-card">
        <div className="label-mini mb-3 text-[#00C8FF]">YourReGen Protocol</div>
        <div className="space-y-2.5">
          {PILLARS.map((p, i) => (
            <div key={p.name} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg bg-[#00C8FF]/10 text-[#00C8FF] flex items-center justify-center font-mono text-xs font-bold flex-shrink-0">{i + 1}</div>
              <div>
                <div className="text-sm font-semibold">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
